/**
 * Intake form field-status helpers.
 *
 * Centralizes "what counts as done" so validation, the green-check indicators,
 * the prefilled styling, and the "still needed" summary all agree on a single
 * definition. Keys are the flat dotted names used by the form's `name`
 * attributes (e.g. `organization_name`, `business_details.business_website`,
 * `address.city`) and map onto the nested `formData` shape.
 */

// The required fields, in the order they appear on the form. `label` is the
// customer-facing name used in the "still needed" summary and error messages.
export const REQUIRED_FIELDS = [
  { key: 'organization_name', label: 'Organization Name' },
  { key: 'contact_name', label: 'Contact Name' },
  { key: 'contact_email', label: 'Contact Email' },
  { key: 'contact_phone', label: 'Contact Phone' },
  { key: 'phone_number', label: 'Phone Number' },
  { key: 'business_details.business_website', label: 'Business Website' },
  { key: 'business_details.business_registration_number', label: 'EIN Registration Number' },
  { key: 'business_details.business_registration_authority', label: 'Registration Authority' },
  { key: 'business_details.business_type', label: 'Business Type' },
  { key: 'business_details.business_registration_country', label: 'Registration Country' },
  { key: 'address.street', label: 'Business Address' },
  { key: 'address.city', label: 'City' },
  { key: 'address.state', label: 'State' },
  { key: 'address.zip', label: 'Zip Code' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Per-field "is this value acceptable" checks beyond simple non-empty.
const VALIDATORS = {
  contact_email: (v) => EMAIL_RE.test(v.trim()),
};

// Message shown when a field has a value but fails its validator (vs. empty).
const INVALID_MESSAGES = {
  contact_email: 'Please enter a valid email address',
};

/**
 * Read a dotted field key out of the nested formData object. Mirrors the
 * branching in IntakeForm's handleChange (`address.` and `business_details.`
 * prefixes), so the address keys resolve to business_details.address.*.
 */
export function getFieldValue(formData, key) {
  if (!formData) return '';
  if (key.startsWith('address.')) {
    const field = key.slice('address.'.length);
    return formData.business_details?.address?.[field] ?? '';
  }
  if (key.startsWith('business_details.')) {
    const field = key.slice('business_details.'.length);
    return formData.business_details?.[field] ?? '';
  }
  return formData[key] ?? '';
}

/** The key used in the `fieldErrors` map (last dotted segment). */
export function errorKeyFor(key) {
  return key.split('.').pop();
}

/** A field is complete when it has a non-empty value that passes its validator. */
export function isFieldComplete(formData, key) {
  const value = getFieldValue(formData, key);
  if (typeof value !== 'string' ? !value : !value.trim()) return false;
  const validate = VALIDATORS[errorKeyFor(key)];
  return validate ? validate(String(value)) : true;
}

/**
 * Resolve the visual state of a field.
 *
 * Returns one of:
 *  - 'error'     — has an entry in fieldErrors (post-submit / duplicate)
 *  - 'complete'  — filled and valid, entered/edited by the customer
 *  - 'prefilled' — filled and valid, still exactly as prefilled (de-emphasized)
 *  - 'missing'   — required and empty
 *  - 'neutral'   — optional and empty (no indicator)
 */
export function getFieldStatus(formData, key, { prefilledFields, fieldErrors, required } = {}) {
  if (fieldErrors && fieldErrors[errorKeyFor(key)]) return 'error';
  const complete = isFieldComplete(formData, key);
  if (complete) {
    return prefilledFields && prefilledFields.has(key) ? 'prefilled' : 'complete';
  }
  return required ? 'missing' : 'neutral';
}

/**
 * Validation message for a required field, or null if it's fine.
 * Distinguishes an empty field ("… is required") from one that has a value but
 * fails its validator (e.g. a malformed email).
 */
export function getFieldError(formData, key, label) {
  const value = getFieldValue(formData, key);
  const hasValue = typeof value === 'string' ? value.trim().length > 0 : !!value;
  if (!hasValue) return `${label} is required`;
  if (!isFieldComplete(formData, key)) {
    return INVALID_MESSAGES[errorKeyFor(key)] || `Please enter a valid ${label}`;
  }
  return null;
}

/** Required fields that are not yet complete, in form order. */
export function getMissingRequiredFields(formData) {
  return REQUIRED_FIELDS.filter((f) => !isFieldComplete(formData, f.key));
}

/** Count of completed required fields (used for the progress tally). */
export function getRequiredProgress(formData) {
  const total = REQUIRED_FIELDS.length;
  const complete = REQUIRED_FIELDS.reduce(
    (n, f) => n + (isFieldComplete(formData, f.key) ? 1 : 0),
    0
  );
  return { complete, total };
}
